import { useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import type { ContractImportFinding } from 'types/contractImport';
import styles from './UploadContracts.module.scss';

const PAGE_SIZE = 20;
const fields: Record<string, string> = {
  employeeNumber: 'Empleado',
  dayOfWeek: 'Día',
  checkInHour: 'Entrada',
  checkOutHour: 'Salida',
  schedule: 'Horario',
  contractCode: 'Contrato',
  file: 'Archivo',
  startDate: 'Fecha de inicio',
  endDate: 'Fecha de fin',
  contractKind: 'Tipo de contrato',
  fechaAntiguedad: 'Antigüedad',
};
const codes: Record<string, string> = {
  EMPLOYEE_NOT_FOUND: 'Empleado inexistente',
  INVALID_EMPLOYEE_NUMBER: 'Número de empleado inválido',
  INVALID_MILITARY_TIME: 'Hora militar inválida',
  REQUIRED_FIELD: 'Campo obligatorio',
  SCHEDULE_OVERLAP: 'Horario solapado',
  INVALID_DAY_OF_WEEK: 'Día inválido',
  EMPTY_FILE: 'Archivo vacío',
  INVALID_FILE: 'Archivo inválido',
  CONTRACT_PROFILE_REQUIREMENT: 'Requisito del perfil',
  INVALID_IMPORT_OPTIONS: 'Datos de importación inválidos',
};
const fieldLabel = (finding: ContractImportFinding) =>
  fields[finding.field] ?? finding.field;
const location = ({ column, row }: ContractImportFinding) =>
  column ? `${column}${row ?? ''}` : 'Sin celda';
const receivedValue = ({ value, field }: ContractImportFinding) => {
  if (field === 'file' && value === null) return 'No aplica';
  return value === null || (typeof value === 'string' && !value.trim())
    ? 'Vacío'
    : String(value);
};
const Explanation = ({ finding }: { finding: ContractImportFinding }) => (
  <>
    <strong className={styles.findingCode}>
      {codes[finding.code] ?? 'Error de validación'}
    </strong>
    <span>{finding.message}</span>
    {!!finding.relatedRows?.length && (
      <span className={styles.relatedRows}>
        Filas relacionadas: {finding.relatedRows.join(', ')}.
      </span>
    )}
  </>
);

export const ImportFindings = ({
  findings,
}: {
  findings: ContractImportFinding[];
}) => {
  const [page, setPage] = useState(1);
  const heading = useRef<HTMLHeadingElement>(null);
  const sorted = useMemo(
    () =>
      [...findings].sort(
        (a, b) =>
          (a.row ?? 0) - (b.row ?? 0) ||
          (a.column ?? '').localeCompare(b.column ?? '')
      ),
    [findings]
  );
  const pages = Math.ceil(sorted.length / PAGE_SIZE);
  const start = (page - 1) * PAGE_SIZE;
  const visible = sorted.slice(start, start + PAGE_SIZE);
  const groups = new Map<number | null, ContractImportFinding[]>();
  visible.forEach((finding) => {
    const group = groups.get(finding.row) ?? [];
    group.push(finding);
    groups.set(finding.row, group);
  });
  const range = `Errores ${start + 1}–${Math.min(start + PAGE_SIZE, sorted.length)} de ${sorted.length}`;
  const changePage = (next: number) => {
    if (!Number.isInteger(next) || next < 1 || next > pages || next === page)
      return;
    setPage(next);
    requestAnimationFrame(() => {
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView({ block: 'start' });
    });
  };

  return (
    <section className={styles.card} data-cy="contract-import-findings">
      <h2 ref={heading} tabIndex={-1} aria-describedby="import-error-range">
        Detalle de errores
      </h2>
      <p id="import-error-range">{range}</p>
      <table className={styles.desktopTable}>
        <caption className={styles.srOnly}>
          Errores del archivo para corregir antes de volver a subirlo
        </caption>
        <thead>
          <tr>
            {[
              'Fila',
              'Empleado',
              'Campo / celda',
              'Valor recibido',
              'Qué corregir',
            ].map((label) => (
              <th scope="col" key={label}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((finding, index) => (
            <tr key={start + index}>
              <td>{finding.row ?? 'Archivo / opciones'}</td>
              <td>{finding.employeeNumber || 'No indicado'}</td>
              <td>
                {fieldLabel(finding)}
                <span className={styles.cell}>{location(finding)}</span>
              </td>
              <td className={styles.value}>{receivedValue(finding)}</td>
              <td>
                <Explanation finding={finding} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.mobileFindings}>
        {[...groups].map(([row, group]) => (
          <section className={styles.findingGroup} key={row ?? 'global'}>
            <h3>{row === null ? 'Archivo / opciones' : `Fila ${row}`}</h3>
            {start > 0 && sorted[start - 1].row === row && (
              <p>Continúa de la página anterior</p>
            )}
            <ul>
              {group.map((finding, index) => (
                <li key={index}>
                  <h4>
                    {fieldLabel(finding)} · {location(finding)}
                  </h4>
                  {finding.employeeNumber && (
                    <p>Empleado: {finding.employeeNumber}</p>
                  )}
                  <dl>
                    <dt>Valor recibido</dt>
                    <dd className={styles.value}>{receivedValue(finding)}</dd>
                  </dl>
                  <Explanation finding={finding} />
                </li>
              ))}
            </ul>
            {sorted[start + PAGE_SIZE]?.row === row && (
              <p>Continúa en la página siguiente</p>
            )}
          </section>
        ))}
      </div>
      {pages > 1 && (
        <nav className={styles.pagination} aria-label="Páginas de errores">
          <p>
            Página {page} de {pages}
          </p>
          <div className={styles.actions}>
            <Button disabled={page === 1} onClick={() => changePage(page - 1)}>
              Anterior
            </Button>
            <Button
              disabled={page === pages}
              onClick={() => changePage(page + 1)}
            >
              Siguiente
            </Button>
          </div>
          <form
            key={page}
            onSubmit={(event) => {
              event.preventDefault();
              changePage(Number(new FormData(event.currentTarget).get('page')));
            }}
          >
            <label htmlFor="import-error-page">Ir a página</label>
            <input
              id="import-error-page"
              name="page"
              type="number"
              min={1}
              max={pages}
              defaultValue={page}
              required
            />
            <Button htmlType="submit">Ir</Button>
          </form>
        </nav>
      )}
    </section>
  );
};
